import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { authApi } from '@/lib/auth.api'
import { apiErrorText } from '@/lib/api-error'
import type { LoginResponse, ZweiterSchritt } from '@/lib/auth.types'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring'

/**
 * AP-38-E — Der zweite Schritt der Anmeldung: Code aus der App, per E-Mail oder
 * ein Wiederherstellungscode. „Dieses Gerät merken" spart ihn 30 Tage lang (8.7)
 * und ist vorbelegt wie in der PWA (Nachtrag H).
 */
export function ZweiterFaktorForm({
  schritt,
  onAngemeldet,
  onZurueck,
}: {
  schritt: Extract<ZweiterSchritt, { schritt: 'ZWEITER_FAKTOR' }>
  onAngemeldet: (data: LoginResponse) => Promise<void>
  onZurueck: () => void
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [wiederherstellung, setWiederherstellung] = useState(false)
  const [merken, setMerken] = useState(true)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)

  const senden = async (e: React.FormEvent) => {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      const { data } = await authApi.verifyTwoFactor(schritt.ticket, code.trim(), merken)
      await onAngemeldet(data)
    } catch (err) {
      setFehler(apiErrorText(err, t('auth.twoFactorCodeInvalid')))
      setLaeuft(false)
    }
  }

  const erneut = async () => {
    setFehler(null)
    try {
      const { data } = await authApi.resendTwoFactor(schritt.ticket)
      setHinweis(t('zweiFaktor.erneutGesendet', { email: data.email }))
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')))
    }
  }

  return (
    <form onSubmit={senden} className="space-y-4">
      <h2 className="text-base font-semibold text-foreground">{t('zweiFaktor.codeTitel')}</h2>
      {fehler && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      )}
      {hinweis && <div className="rounded-md bg-muted px-4 py-3 text-sm text-foreground">{hinweis}</div>}
      <p className="text-sm text-muted-foreground">
        {wiederherstellung
          ? t('zweiFaktor.wiederherstellungHinweis')
          : schritt.verfahren === 'EMAIL'
            ? t('zweiFaktor.codeHinweisEmail', { email: schritt.email ?? '' })
            : t('zweiFaktor.codeHinweisTotp')}
      </p>
      <input
        autoFocus
        key={wiederherstellung ? 'w' : 'c'}
        inputMode={wiederherstellung ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        aria-label={t('zweiFaktor.code')}
        placeholder={wiederherstellung ? 'abcd-efgh' : '123456'}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className={`${eingabe} text-center font-mono text-lg tracking-[0.3em]`}
      />
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5" checked={merken} onChange={(e) => setMerken(e.target.checked)} />
        <span>
          <span className="block text-foreground">{t('zweiFaktor.geraetMerken')}</span>
          <span className="block text-xs text-muted-foreground">{t('zweiFaktor.geraetMerkenHinweis')}</span>
        </span>
      </label>
      <Button type="submit" className="w-full" disabled={laeuft || code.trim().length < 6}>
        {laeuft ? t('common.loading') : t('zweiFaktor.anmelden')}
      </Button>
      <div className="flex flex-wrap justify-between gap-2 text-xs">
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={() => {
            setWiederherstellung((w) => !w)
            setCode('')
          }}
        >
          {wiederherstellung ? t('zweiFaktor.normalenCodeVerwenden') : t('zweiFaktor.wiederherstellungVerwenden')}
        </button>
        {schritt.verfahren === 'EMAIL' && !wiederherstellung && (
          <button type="button" className="text-primary hover:underline" onClick={() => void erneut()}>
            {t('zweiFaktor.erneutSenden')}
          </button>
        )}
        <button type="button" className="text-muted-foreground hover:underline" onClick={onZurueck}>
          {t('zweiFaktor.zurueck')}
        </button>
      </div>
    </form>
  )
}
